import { FC, ReactNode, useState, Suspense, useEffect, useCallback, useContext } from 'react';
import { FormattedMessage, useIntl } from 'dibs-react-intl';
import { graphql, useQueryLoader, PreloadedQuery, usePreloadedQuery } from 'react-relay';
import { useDispatch } from 'react-redux';

import { ServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { LOCALE_US } from 'dibs-intl/exports/locales';
import { FormattedPrice } from 'dibs-buyer-price';
import ShippingIcon from 'dibs-icons/exports/legacy/Shipping';
import { PdpSharedShippingInfoLink } from './PdpSharedShippingInfoLink';

import { FREE_SHIPPING_AND_COST } from 'dibs-search-product-tile/exports/constants';
import { useShippingPrice } from 'dibs-search-product-tile/exports/useShippingPrice';
import { getUserGeoInfo } from 'dibs-regional-info/exports/regionalInfoHelpers';
import { useAbTestV2BuyerUser } from 'dibs-buyer-layout/exports/clientABTestV2';
import { useShippingType, ShippingType } from './hooks/useShippingType';
import { excludeShippingPromos } from './excludeShippingPromos';
import { PdpSharedPromoCode } from '../PdpSharedPromoCode/PdpSharedPromoCode';
import PdpSharedShippingInfoLoading from './PdpSharedShippingInfoLoading';
import { updateRelayVariables } from '../../actions/sharedRelayVariableActions';
import { PdpSharedLowerShippingInfo } from '../PdpSharedLowerShippingInfo/PdpSharedLowerShippingInfo';
import { useUserShippingRegionsUrlLabels } from '../PdpSharedLowerShippingInfo/hooks/useUserShippingRegionsUrlLabels';
import { PdpSharedShippingAddress } from './PdpSharedShippingAddress';
import { isUS } from 'dibs-request-shipping-modal';

import classnames from 'classnames';
import styles from './PdpSharedShippingInfo.scss';

import {
    PdpSharedShippingInfoSidebarQuery as QueryType,
    PdpSharedShippingInfoSidebarQuery$variables,
} from './__generated__/PdpSharedShippingInfoSidebarQuery.graphql';

export const PdpSharedShippingInfoSidebarQuery = graphql`
    query PdpSharedShippingInfoSidebarQuery(
        $itemId: String!
        $userId: String!
        $fetchRegionalInfo: Boolean = false
        $userZipCode: String = ""
        $userCountryCode: String = ""
        $isTrade: Boolean = false
        $fetchItem: Boolean = false
        $fetchUser: Boolean = false
        $isClient: Boolean = false
        $itemQuantity: Int
        $shippingRegions: [String] = []
        $calculateLowerShippingInfo: Boolean = false
    ) {
        viewer {
            regionSelectorEnabled: featureFlag(feature: "pdpRegionSelector")
            ...PdpSharedShippingHolidayDelivery_viewer
            ...PdpSharedShippingInfoLink_viewer
            ...PdpSharedPromoCode_viewer
            ...PdpSharedLowerShippingInfo_viewer
                @arguments(
                    fetchRegionalInfo: $fetchRegionalInfo
                    userZipCode: $userZipCode
                    userCountryCode: $userCountryCode
                )
                @include(if: $calculateLowerShippingInfo)
            ...PdpSharedShippingAddress_viewer
            relevantShipping(
                itemId: $itemId
                buyerId: $userId
                quantity: $itemQuantity
                zipCode: $userZipCode
                countryCode: $userCountryCode
            ) {
                quoteRequestStatus
                shipmentQuote {
                    ...PdpSharedShippingInfoLink_shipmentQuote
                    subTotalAmount {
                        convertedAmountList {
                            currency
                            amount
                        }
                    }
                    buyerServiceMethodDisplay
                    showQuoteAsPending
                    serviceMethod {
                        serviceType
                    }
                    author {
                        role
                    }
                    isShipmentSubsidizedByDibs
                    isShipmentSubsidized
                }
                shipment {
                    shippingAddress {
                        displayCityStateCountry
                    }
                }
            }

            ...useShippingPrice_viewer
                @arguments(
                    fetchRegionalInfo: $fetchRegionalInfo
                    userZipCode: $userZipCode
                    userCountryCode: $userCountryCode
                )
            ...useShippingType_viewer
                @arguments(
                    userId: $userId
                    itemId: $itemId
                    fetchRegionalInfo: $fetchRegionalInfo
                    userZipCode: $userZipCode
                    userCountryCode: $userCountryCode
                    itemQuantity: $itemQuantity
                )

            itemRead(itemIds: [$itemId]) @include(if: $fetchItem) {
                ... on Item {
                    serviceId
                    isJewelry
                    address {
                        country
                    }
                    ...useShippingPrice_item
                        @arguments(
                            buyerId: $userId
                            fetchShippingCostInfo: true
                            itemQuantity: $itemQuantity
                        )
                    ...PdpSharedLowerShippingInfo_item
                        @arguments(
                            isTrade: $isTrade
                            buyerId: $userId
                            fetchShippingCostInfo: true
                            itemQuantity: $itemQuantity
                            userCountryCode: $userCountryCode
                            shippingRegions: $shippingRegions
                        )
                        @include(if: $calculateLowerShippingInfo)
                    ...useShippingType_item @arguments(userId: $userId, itemQuantity: $itemQuantity)
                    ...PdpSharedShippingInfoLink_item
                    ...PdpSharedShippingInfoWrapper_item
                }
            }

            user(userId: $userId) @include(if: $fetchUser) {
                serviceId
                addressBook {
                    proAddress {
                        displayCountry
                        zipCode
                        country
                    }
                    personalAddress {
                        displayCountry
                        zipCode
                        country
                    }
                    addressList {
                        displayCountry
                        zipCode
                        country
                    }
                }
                ...PdpSharedPromoCode_user
                ...PdpSharedShippingInfoLink_user
                ...PdpSharedShippingAddress_user
                ...clientAbTestV2_useAbTestV2BuyerUser_user
            }
        }
    }
`;

export const AVAILABLE_NOW = 'available-now';

export const REQUEST_STATUS = {
    REQUEST_QUOTE: 'requestQuote',
    CUSTOM_PROVIDED: 'customQuoteProvided',
    CUSTOM_REQUESTED: 'customQuoteRequested',
};

const getShippingTypeMessage = ({
    price,
    fullPrice,
    currency,
    shippingType,
}: {
    price: number | null;
    fullPrice: number;
    currency: string;
    shippingType: ShippingType;
}): ReactNode => {
    return (
        <FormattedMessage
            id="pdp.PdpSharedShippingInfoSidebarComponent.shippingTypeSidebar"
            defaultMessage="{shippingTypeRef, select,
            paid {{shippingPrice}}
            paid_discounted {{discountPrice} {styledShippingPrice}}
            other {}
        }"
            values={{
                shippingTypeRef: shippingType,
                discountPrice: (
                    <span className={styles.discountedPrice}>
                        <FormattedPrice currency={currency} value={fullPrice} />
                    </span>
                ),
                shippingPrice: (
                    <span>
                        <FormattedPrice currency={currency} value={price || 0} />
                    </span>
                ),
                styledShippingPrice: (
                    <span className={styles.salePrice}>
                        <FormattedPrice currency={currency} value={price || 0} />
                    </span>
                ),
            }}
        />
    );
};

type PdpSharedShippingInfoSidebarProps = {
    itemId: string;
    userId: string;
    currency: string;
    preLinkQuoteDetails?: ReactNode;
    postLinkQuoteDetails?: ReactNode;
    postLinkNoQuoteDetails?: ReactNode;
    title?: ReactNode;
    loadingTitle?: ReactNode;
    isTrade?: boolean;
    isClient?: boolean;
    itemQuantity?: number | null;
    children?: ReactNode;
    calculateLowerShippingInfo?: boolean;
};

type PdpSharedShippingInfoSidebarComponentProps = {
    queryReference: PreloadedQuery<QueryType>;
    currency: string;
    preLinkQuoteDetails?: ReactNode;
    postLinkQuoteDetails?: ReactNode;
    postLinkNoQuoteDetails?: ReactNode;
    title?: ReactNode;
    loadingTitle?: ReactNode;
    reloadQuery?: (args: Partial<PdpSharedShippingInfoSidebarQuery$variables>) => void;
    itemQuantity?: number | null;
    children?: ReactNode;
    calculateLowerShippingInfo?: boolean;
};

export const PdpSharedShippingInfoSidebarComponent: FC<
    PdpSharedShippingInfoSidebarComponentProps
> = ({
    currency,
    preLinkQuoteDetails,
    postLinkQuoteDetails,
    postLinkNoQuoteDetails,
    title,
    loadingTitle,
    queryReference,
    reloadQuery,
    itemQuantity,
    children,
    calculateLowerShippingInfo = false,
}) => {
    const { locale } = useIntl();
    const { isMobile } = useContext(ServerVarsContext);
    const [showSpinner, setShowSpinner] = useState(true);
    const { viewer } = usePreloadedQuery<QueryType>(
        PdpSharedShippingInfoSidebarQuery,
        queryReference
    );
    const { relevantShipping, itemRead, user = null } = viewer || {};
    const item = itemRead?.[0] || null;
    const { serviceId: itemId, isJewelry } = item || {};
    const { shipment, shipmentQuote, quoteRequestStatus } = relevantShipping || {};
    const isRegionSelectorEnabled = locale !== LOCALE_US && viewer?.regionSelectorEnabled;
    const userGeoInfo = getUserGeoInfo();

    const {
        subTotalAmount,
        buyerServiceMethodDisplay: shippingMethodDisplay,
        showQuoteAsPending,
        serviceMethod,
        author,
        isShipmentSubsidizedByDibs,
        isShipmentSubsidized,
    } = shipmentQuote || {};

    const customQuoteShippingAddress = shipment?.shippingAddress?.displayCityStateCountry;

    const shippingType = useShippingType({ viewer, item, currency });

    const price = useShippingPrice({
        viewer,
        item,
        currency,
        type: FREE_SHIPPING_AND_COST,
    });

    const fullPrice =
        subTotalAmount?.convertedAmountList?.find(p => p?.currency === currency)?.amount || 0;

    const showQuoteDetails = quoteRequestStatus !== 'requestQuote';
    const { proAddress, personalAddress } = user?.addressBook || {};
    const userLocation =
        userGeoInfo?.countryCode || proAddress?.country || personalAddress?.country;
    const itemLocation = item?.address?.country;

    const isFreeQuoteToBuyer = isShipmentSubsidizedByDibs || isShipmentSubsidized;

    useAbTestV2BuyerUser({
        testName: 'RTQuote-Prequote-US-Q3-2023',
        user,
        shouldFireTracking:
            isUS(userLocation) &&
            isUS(itemLocation) &&
            author?.role !== 'SELLER' &&
            serviceMethod?.serviceType === 'PARCEL' &&
            !isFreeQuoteToBuyer &&
            !isJewelry,
    });

    let hasShippingPill = false;
    let hasRequestedQuote = false;
    let overviewMessage;
    if (!shipmentQuote || quoteRequestStatus === REQUEST_STATUS.REQUEST_QUOTE) {
        hasRequestedQuote = true;
        overviewMessage = (
            <FormattedMessage
                id="pdp.PdpSharedBlurbShippingCustomQuote.overviewRequestQuote.sidebar"
                defaultMessage="<strong>Shipping:</strong> Rates vary by destination and complexity."
                values={{
                    strong: message => <strong>{message}</strong>,
                }}
            />
        );
    } else if (quoteRequestStatus === REQUEST_STATUS.CUSTOM_REQUESTED || showQuoteAsPending) {
        hasRequestedQuote = true;
        overviewMessage = (
            <span data-tn="quote-requested">
                <FormattedMessage
                    id="pdp.PdpSharedBlurbShippingCustomQuote.overviewPendingDetails.sidebar"
                    defaultMessage="<strong>Shipping:</strong> Quote pending to {customQuoteShippingAddress}."
                    values={{
                        strong: message => <strong>{message}</strong>,
                        customQuoteShippingAddress,
                    }}
                />
            </span>
        );
    } else {
        if (shippingType === 'free' || shippingType === 'free_discounted') {
            hasShippingPill = true;
        }
        const regionMessage = (
            <FormattedMessage
                id="pdp.PdpSharedShippingInfoSidebar.prequoteOverviewRegion"
                defaultMessage="{isIntl, select,
                        true {{region}}
                        other {}
                    }"
                values={{
                    isIntl: locale !== LOCALE_US,
                    region: (
                        <PdpSharedShippingAddress
                            viewer={viewer}
                            user={user}
                            onAddressUpdate={reloadQuery}
                            positioning="sidebar"
                        />
                    ),
                }}
            />
        );
        overviewMessage = (
            <span data-tn="prequote-overview">
                <FormattedMessage
                    id="pdp.PdpSharedShippingInfoSidebar.prequoteOverview"
                    defaultMessage="{shippingType, select,
                        free {<detailsLink>Free Shipping</detailsLink>}
                        free_discounted {<detailsLink>Free Shipping</detailsLink>}
                        paid {<strong>Shipping:</strong> {shippingMessage} {method} {regionMessage}}
                        paid_discounted {<strong>Shipping:</strong> {shippingMessage} {method} {regionMessage}}
                        other {}
                    }"
                    values={{
                        shippingType,
                        shippingMessage: getShippingTypeMessage({
                            price,
                            fullPrice,
                            currency,
                            shippingType,
                        }),
                        method: shippingMethodDisplay,
                        strong: message => <strong>{message}</strong>,
                        detailsLink: child => (
                            <PdpSharedShippingInfoLink
                                currency={currency}
                                shipmentQuote={shipmentQuote}
                                userId={user?.serviceId || ''}
                                item={item}
                                itemId={itemId || ''}
                                user={user}
                                viewer={viewer}
                                showQuoteDetails={showQuoteDetails}
                                quoteRequestStatus={quoteRequestStatus}
                                onSuccessfulPreQuoteRequest={reloadQuery}
                                displayInline
                                itemQuantity={itemQuantity}
                            >
                                <div className={styles.freeShippingPill}>{child}</div>
                            </PdpSharedShippingInfoLink>
                        ),
                        regionMessage,
                    }}
                />
            </span>
        );
    }

    useEffect(() => {
        if (showSpinner) {
            const timer = setTimeout(() => setShowSpinner(false), 2000);
            return () => clearTimeout(timer);
        }
        return () => {};
    }, [showSpinner]);

    if (showSpinner) {
        return (
            <PdpSharedShippingInfoLoading title={loadingTitle}>
                {children}
            </PdpSharedShippingInfoLoading>
        );
    }

    return (
        <div data-tn="pdp-shared-shipping-info" className={styles.shippingContainer}>
            {!hasShippingPill && (
                <div className={styles.shippingIcon}>
                    <ShippingIcon />
                </div>
            )}
            <div
                className={classnames(styles.shippingInfoContainer, {
                    [styles.intlInline]: isRegionSelectorEnabled && hasShippingPill,
                })}
            >
                {isRegionSelectorEnabled && (hasShippingPill || hasRequestedQuote) && (
                    <PdpSharedShippingAddress
                        viewer={viewer}
                        user={user}
                        onAddressUpdate={reloadQuery}
                        positioning="sidebar"
                    />
                )}
                <FormattedMessage
                    id="pdp.PdpSharedShippingInfoSidebar.infoContainer"
                    defaultMessage={`{showTitle, select,
                        true {{title}: {content}}
                        other {{content}}
                    }`}
                    values={{
                        showTitle: price !== 0 && !!title,
                        title,
                        content: (
                            <div
                                className={classnames(
                                    styles.shippingPromoContainer,
                                    styles.reverse
                                )}
                            >
                                <PdpSharedPromoCode
                                    onlyShippingPromos
                                    excludeShippingPromos={excludeShippingPromos(shippingType)}
                                    itemId={itemId || ''}
                                    viewer={viewer}
                                    user={user}
                                    userId={user?.serviceId || ''}
                                    displayInfo={null}
                                    currency={currency}
                                />
                                <div>
                                    {overviewMessage} {showQuoteDetails && preLinkQuoteDetails}
                                    {!hasShippingPill && (
                                        <PdpSharedShippingInfoLink
                                            currency={currency}
                                            shipmentQuote={shipmentQuote}
                                            userId={user?.serviceId || ''}
                                            item={item}
                                            itemId={itemId || ''}
                                            user={user}
                                            viewer={viewer}
                                            showQuoteDetails={showQuoteDetails}
                                            quoteRequestStatus={quoteRequestStatus}
                                            onSuccessfulPreQuoteRequest={reloadQuery}
                                            displayInline
                                            itemQuantity={itemQuantity}
                                            isMobile={isMobile}
                                            isSidebar
                                            wrapperClass={styles.shippingModalLinkWrapper}
                                        />
                                    )}
                                    {showQuoteDetails && postLinkQuoteDetails}
                                    {!showQuoteDetails && postLinkNoQuoteDetails}
                                </div>
                            </div>
                        ),
                    }}
                />
                {children}
                {/* PdpSharedLowerShippingInfo can't be passed as children because shipping cost calculation hapens on client side graphql call  */}
                {calculateLowerShippingInfo && (
                    <PdpSharedLowerShippingInfo viewer={viewer} item={item} currency={currency} />
                )}
            </div>
        </div>
    );
};

export const PdpSharedShippingInfoSidebar: FC<PdpSharedShippingInfoSidebarProps> = ({
    isClient = false,
    itemId,
    userId,
    isTrade = false,
    currency,
    preLinkQuoteDetails,
    postLinkQuoteDetails,
    postLinkNoQuoteDetails,
    title,
    loadingTitle,
    itemQuantity,
    children,
    calculateLowerShippingInfo,
}) => {
    const [queryReference, loadQuery] = useQueryLoader<QueryType>(
        PdpSharedShippingInfoSidebarQuery
    );
    const dispatch = useDispatch();
    const shippingRegions = useUserShippingRegionsUrlLabels();

    const reloadQuery = useCallback(
        (variables: Partial<PdpSharedShippingInfoSidebarQuery$variables>) => {
            const userGeoInfo = getUserGeoInfo();
            loadQuery(
                {
                    itemId: itemId || '',
                    userId: userId || '',
                    isTrade,
                    fetchUser: !!userId,
                    fetchItem: !!itemId,
                    isClient,
                    itemQuantity,
                    userCountryCode: userGeoInfo?.countryCode || '',
                    userZipCode: userGeoInfo?.zipCode || '',
                    shippingRegions,
                    calculateLowerShippingInfo,
                    // We want to override query variables with the new ones when refetching the query
                    ...variables,
                },
                { fetchPolicy: 'network-only' }
            );
            dispatch(
                updateRelayVariables({
                    updateItemPricing: true,
                    updatedUserCountryCode: variables?.userCountryCode,
                })
            );
        },
        [
            loadQuery,
            dispatch,
            itemId,
            userId,
            isTrade,
            isClient,
            itemQuantity,
            shippingRegions,
            calculateLowerShippingInfo,
        ]
    );

    useEffect(() => {
        if (isClient) {
            const userGeoInfo = getUserGeoInfo();
            loadQuery(
                {
                    itemId: itemId || '',
                    userId: userId || '',
                    isTrade,
                    fetchUser: !!userId,
                    fetchItem: !!itemId,
                    isClient,
                    userCountryCode: userGeoInfo?.countryCode || '',
                    userZipCode: userGeoInfo?.zipCode || '',
                    itemQuantity,
                    shippingRegions,
                    calculateLowerShippingInfo,
                    fetchRegionalInfo: false,
                },
                { fetchPolicy: 'network-only' }
            );
        }
    }, [
        loadQuery,
        isClient,
        itemId,
        userId,
        isTrade,
        itemQuantity,
        shippingRegions,
        calculateLowerShippingInfo,
    ]);

    if (!queryReference) {
        return (
            <PdpSharedShippingInfoLoading title={loadingTitle}>
                {children}
            </PdpSharedShippingInfoLoading>
        );
    }

    return (
        <Suspense
            fallback={
                <PdpSharedShippingInfoLoading title={loadingTitle}>
                    {children}
                </PdpSharedShippingInfoLoading>
            }
        >
            <PdpSharedShippingInfoSidebarComponent
                queryReference={queryReference}
                currency={currency}
                title={title}
                loadingTitle={loadingTitle}
                preLinkQuoteDetails={preLinkQuoteDetails}
                postLinkQuoteDetails={postLinkQuoteDetails}
                postLinkNoQuoteDetails={postLinkNoQuoteDetails}
                reloadQuery={reloadQuery}
                itemQuantity={itemQuantity}
                calculateLowerShippingInfo={calculateLowerShippingInfo}
            >
                {children}
            </PdpSharedShippingInfoSidebarComponent>
        </Suspense>
    );
};
